import React from 'react'
import type { WeatherQuery } from '@paintscout/api'
import { makeStyles, Typography, useIsMobile } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import WbSunnyIcon from '@material-ui/icons/WbSunny'
import CloudIcon from '@material-ui/icons/Cloud'
import AcUnitIcon from '@material-ui/icons/AcUnit'
import GrainIcon from '@material-ui/icons/Grain'
import OpacityIcon from '@material-ui/icons/Opacity'
import classNames from 'classnames'

export type ZoomLevel = 'days' | 'weeks' | 'months' | 'quarters'

const useStyles = makeStyles<Theme, TimelineHeaderProps>(
  (theme) => {
    const BORDER_SLATE = `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
    return {
      root: {},
      timelineNavigationHeader: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        borderBottom: BORDER_SLATE, // slate-200/60
        backgroundColor: '#fff' // bg-white
      },
      container: {
        backgroundColor: '#fff' // bg-white
      },
      flex: {
        display: 'flex'
      },
      flex1: {
        flex: 1
      },
      header: {
        width: 256,
        flexShrink: 0,
        position: 'sticky',
        left: 0,
        zIndex: 20,
        backgroundColor: '#fff',
        border: BORDER_SLATE // border-slate-200/60
      },
      dayBlock: {
        width: 80,
        flexShrink: 0,
        boxSizing: 'border-box',
        backgroundColor: '#fff',
        padding: theme.spacing(0.75),
        transition: 'all 0.2s',
        height: 96,
        [theme.breakpoints.down('xs')]: {
          height: 56,
          padding: theme.spacing(0.5)
        }
      },
      dayBlockCompact: {
        height: 64,
        [theme.breakpoints.down('xs')]: {
          height: 56
        }
      },
      dayBlockWeekendBoundary: {
        // Vertical divider only at weekend boundaries — Fri|Sat, Sat|Sun, Sun|Mon
        borderLeft: BORDER_SLATE
      },
      todayBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#2563eb',
        color: '#ffffff',
        borderRadius: theme.borderRadius.md,
        padding: theme.spacing(0, 0.75),
        minWidth: theme.spacing(3),
        height: theme.spacing(3),
        lineHeight: 1
      },
      isTodayBackgroundColor: {
        backgroundColor: '#e3f1fb' // semanticPalette.Info.S95
      },
      isTodayWeekendBackgroundColor: {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P98
      },
      isHoveredDayText: {
        backgroundColor: '#f8fafc' // slate-50
      },
      weatherTextContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: '100%',
        paddingBottom: theme.spacing(0.5)
      },
      weatherTextContainerCompact: {
        justifyContent: 'center',
        paddingBottom: 0
      },
      mobileDayDateRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(0.5),
        // Fixed height matches the today-badge so rows with/without the badge
        // share the same baseline and the weather icons line up below.
        height: theme.spacing(3),
        marginBottom: theme.spacing(0.5),
        color: `${theme.paintscout.palette.brand.Neutral.P30} !important`,
        '& > *': {
          color: `${theme.paintscout.palette.brand.Neutral.P30} !important`,
          fontWeight: `500 !important` as any,
          fontSize: theme.typography.pxToRem(13)
        },
        // Today badge keeps its white text on the blue background — override
        // the dark color from the rule above.
        '& > $todayBadge, & > $todayBadge *': {
          color: `#ffffff !important`
        }
      },
      weatherIconContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(0.5),
        whiteSpace: 'nowrap',
        lineHeight: 1
      },
      weatherDivider: {
        width: '50%',
        height: 1,
        backgroundColor: 'rgba(226, 232, 240, 0.8)',
        marginTop: 'auto',
        marginBottom: 'auto',
        marginLeft: 'auto',
        marginRight: 'auto'
      },
      weatherIcon: {
        // className={`w-4 h-4 ${weather.color}`}
        width: theme.spacing(1.75),
        height: theme.spacing(1.75)
        // color: '#fbbf24' // yellow-400
      },
      weatherTemp: {
        fontSize: '0.8rem',
        lineHeight: 1.1
      },
      weatherPlaceholder: {
        // Reserve the space the weather row would occupy so the day-cell layout stays
        // stable when weather data is not available or the feature is disabled.
        marginTop: 'auto',
        width: '100%',
        height: theme.spacing(2.5),
        boxSizing: 'border-box'
      },
      yellow: {
        color: '#fbbf24' // yellow-400
      },
      blue: {
        color: '#3b82f6' // blue-500
      },
      gray: {
        color: '#6b7280' // gray-500
      },
      navigationHeader: {
        width: 256,
        flexShrink: 0,
        borderRight: BORDER_SLATE, // border-slate-200/60
        background: 'linear-gradient(to bottom, rgba(248,250,252,0.5), #fff)', // from-slate-50/50 to-white
        position: 'sticky',
        left: 0,
        zIndex: 20,
        boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)'
      },
      navigationSpan: {
        display: 'flex',
        flex: 1,
        background: 'linear-gradient(to bottom, rgba(248,250,252,0.5), #fff)', // from-slate-50/50 to-white
        borderBottom: BORDER_SLATE, // border-slate-200/60
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: theme.spacing(0.5)
      }
    }
  },
  { name: 'TimelineHeader' }
)

export function getLocalDateString(date: Date) {
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

type WeatherDay = WeatherQuery['weather'][number]

const hasTemperature = (point: WeatherDay) => {
  return (
    typeof point.temperature === 'number' ||
    typeof point.temperatureMax === 'number' ||
    typeof point.temperatureMin === 'number'
  )
}

const TOMORROW_CLEAR_CODES = new Set([1000, 1100])
const TOMORROW_PARTLY_CLOUDY_CODES = new Set([1101, 1102, 1001])
const TOMORROW_FOG_CODES = new Set([2000, 2100])
const TOMORROW_DRIZZLE_CODES = new Set([4000, 4200])
const TOMORROW_RAIN_CODES = new Set([4001, 4201, 8000])
const TOMORROW_SNOW_CODES = new Set([5000, 5001, 5100, 5101])
const TOMORROW_FREEZING_CODES = new Set([6000, 6001, 6200, 6201, 7000, 7101, 7102])

const getWeatherForPoint = (point: WeatherDay) => {
  const tempValue = point.temperature ?? point.temperatureMax ?? point.temperatureMin
  const roundedTemp = typeof tempValue === 'number' ? Math.round(tempValue) : null
  const weatherCode = point.weatherCode ?? null
  const precipitation = point.precipitationProbability ?? 0

  if (typeof weatherCode === 'number') {
    if (TOMORROW_SNOW_CODES.has(weatherCode) || TOMORROW_FREEZING_CODES.has(weatherCode)) {
      return { icon: AcUnitIcon, temp: roundedTemp, label: 'Snow', color: 'blue' }
    }
    if (TOMORROW_RAIN_CODES.has(weatherCode)) {
      return { icon: GrainIcon, temp: roundedTemp, label: 'Rain', color: 'blue' }
    }
    if (TOMORROW_DRIZZLE_CODES.has(weatherCode)) {
      return { icon: OpacityIcon, temp: roundedTemp, label: 'Drizzle', color: 'blue' }
    }
    if (TOMORROW_CLEAR_CODES.has(weatherCode)) {
      return { icon: WbSunnyIcon, temp: roundedTemp, label: 'Sunny', color: 'yellow' }
    }
    if (TOMORROW_PARTLY_CLOUDY_CODES.has(weatherCode) || TOMORROW_FOG_CODES.has(weatherCode)) {
      return { icon: CloudIcon, temp: roundedTemp, label: 'Cloudy', color: 'gray' }
    }
  }

  if (typeof tempValue !== 'number') {
    return { icon: CloudIcon, temp: null, label: 'Weather', color: 'gray' }
  }

  const isSnow = tempValue <= 34 && precipitation >= 20
  if (isSnow) {
    return { icon: AcUnitIcon, temp: roundedTemp, label: 'Snow', color: 'blue' }
  }

  if (precipitation >= 60) {
    return { icon: GrainIcon, temp: roundedTemp, label: 'Rain', color: 'blue' }
  }

  if (precipitation >= 30) {
    return { icon: OpacityIcon, temp: roundedTemp, label: 'Drizzle', color: 'blue' }
  }

  if (tempValue >= 75) {
    return { icon: WbSunnyIcon, temp: roundedTemp, label: 'Sunny', color: 'yellow' }
  }

  if (tempValue <= 55) {
    return { icon: CloudIcon, temp: roundedTemp, label: 'Cloudy', color: 'gray' }
  }

  return { icon: CloudIcon, temp: roundedTemp, label: 'Partly Cloudy', color: 'gray' }
}

export interface TimelineHeaderProps {
  classes?: StyleClasses<typeof useStyles>
  startDate: Date
  endDate: Date
  zoomLevel: ZoomLevel
  cellWidth?: number
  hoveredDate?: string | null
  onDateHover?: (date: string | null) => void
  weatherByDate?: Record<string, WeatherDay>
  temperatureUnit?: 'F' | 'C'
  hideWeather?: boolean
}

// Mock weather data generator
function TimelineHeader(props: TimelineHeaderProps) {
  const {
    startDate,
    endDate,
    zoomLevel,
    cellWidth = 80,
    hoveredDate,
    onDateHover,
    weatherByDate,
    temperatureUnit = 'F',
    hideWeather = false
  } = props
  const classes = useStyles(props)
  const isMobile = useIsMobile({ xs: true })
  const isCompact = hideWeather || zoomLevel !== 'days'
  const today = new Date(Date.now())
  today.setHours(0, 0, 0, 0)
  const formatTemperature = (value: number | null) => {
    if (typeof value !== 'number') return null
    if (temperatureUnit === 'C') {
      return Math.round(((value - 32) * 5) / 9)
    }
    return value
  }

  const isTodayInPeriod = (periodStart: Date, periodEnd: Date) => {
    return today >= periodStart && today <= periodEnd
  }

  if (zoomLevel === 'days') {
    const days: Date[] = []
    const currentDate = new Date(startDate)

    while (currentDate <= endDate) {
      days.push(new Date(currentDate))
      currentDate.setDate(currentDate.getDate() + 1)
    }

    const formatDay = (date: Date) => {
      if (isMobile) {
        // Mobile: single-letter day (M, T, W, T, F, S, S).
        return date.toLocaleDateString('en-US', { weekday: 'narrow' })
      }
      return date.toLocaleDateString('en-US', { weekday: 'short' })
    }

    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', { day: 'numeric' })
    }

    const getMonthName = (date: Date) => {
      return date.toLocaleDateString('en-US', { month: 'short' })
    }

    const isWeekend = (date: Date) => {
      const day = date.getDay()
      return day === 0 || day === 6
    }

    // Group days by month for month headers
    const monthGroups: { month: string; startIndex: number; count: number }[] = []
    let currentMonth = ''
    let monthStartIndex = 0

    days.forEach((date, index) => {
      const month = getMonthName(date)
      if (month !== currentMonth) {
        if (currentMonth) {
          monthGroups.push({ month: currentMonth, startIndex: monthStartIndex, count: index - monthStartIndex })
        }
        currentMonth = month
        monthStartIndex = index
      }
    })

    // Add the last month group
    if (currentMonth) {
      monthGroups.push({ month: currentMonth, startIndex: monthStartIndex, count: days.length - monthStartIndex })
    }

    return (
      <div className={classes.container}>
        {/* Day headers */}
        <div className={classes.flex}>
          <div className={`${classes.flex} ${classes.flex1}`}>
            {days.map((date) => {
              const isToday = date.toDateString() === today.toDateString()
              const dateStr = getLocalDateString(date)
              const point = weatherByDate?.[dateStr]
              const weather = point && hasTemperature(point) ? getWeatherForPoint(point) : null
              const WeatherIcon = weather?.icon
              const isHovered = hoveredDate === dateStr
              const isWeekendDay = isWeekend(date)
              const dayOfWeek = date.getDay()
              // Frame the weekend block: Sat=6 (Fri|Sat), Sun=0 (Sat|Sun), Mon=1 (Sun|Mon)
              const isWeekendBoundary = dayOfWeek === 0 || dayOfWeek === 1 || dayOfWeek === 6
              return (
                <div
                  key={dateStr}
                  data-date={dateStr}
                  onMouseEnter={() => onDateHover?.(dateStr)}
                  onMouseLeave={() => onDateHover?.(null)}
                  style={{ width: `${cellWidth}px` }}
                  className={classNames({
                    [classes.dayBlock]: true,
                    [classes.dayBlockCompact]: isCompact,
                    [classes.dayBlockWeekendBoundary]: isWeekendBoundary,
                    [classes.isTodayBackgroundColor]: isToday,
                    [classes.isTodayWeekendBackgroundColor]: isWeekendDay && !isToday,
                    [classes.isHoveredDayText]: isHovered && !isToday && !isWeekendDay
                  })}
                >
                  <div
                    className={classNames(classes.weatherTextContainer, {
                      [classes.weatherTextContainerCompact]: isCompact
                    })}
                  >
                    {isMobile ? (
                      <div className={classes.mobileDayDateRow}>
                        <Typography variant="subtitle2">{formatDay(date)}</Typography>
                        {isToday ? (
                          <span className={classes.todayBadge}>
                            <Typography variant="subtitle2" style={{ color: 'inherit' }}>
                              {formatDate(date)}
                            </Typography>
                          </span>
                        ) : (
                          <Typography variant="subtitle2">{formatDate(date)}</Typography>
                        )}
                      </div>
                    ) : (
                      <>
                        <Typography variant="subtitle2">{formatDay(date)}</Typography>
                        <div style={{ marginTop: 4 }}>
                          {isToday ? (
                            <span className={classes.todayBadge}>
                              <Typography variant="h4" style={{ color: 'inherit' }}>
                                {formatDate(date)}
                              </Typography>
                            </span>
                          ) : (
                            <Typography variant="h4">{formatDate(date)}</Typography>
                          )}
                        </div>
                        {!hideWeather && <div className={classes.weatherDivider} aria-hidden />}
                      </>
                    )}
                    {!hideWeather &&
                      (weather && WeatherIcon ? (
                        <div className={classes.weatherIconContainer}>
                          <WeatherIcon classes={{ root: `${classes[weather.color]} ${classes.weatherIcon}` }} />
                          {!isMobile && typeof weather.temp === 'number' && (
                            <Typography variant="body2" className={classes.weatherTemp}>
                              {formatTemperature(weather.temp)}°{temperatureUnit}
                            </Typography>
                          )}
                        </div>
                      ) : (
                        <div className={classes.weatherPlaceholder} aria-hidden />
                      ))}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  if (zoomLevel === 'weeks') {
    const weeks: Array<{ start: Date; end: Date }> = []
    const currentDate = new Date(startDate)

    while (currentDate <= endDate) {
      const weekStart = new Date(currentDate)
      const weekEnd = new Date(currentDate)
      weekEnd.setDate(weekEnd.getDate() + 6)

      weeks.push({ start: new Date(weekStart), end: new Date(weekEnd) })
      currentDate.setDate(currentDate.getDate() + 7)
    }

    const formatWeekLabel = (start: Date) => {
      return start.toLocaleDateString('en-US', { month: 'short' })
    }

    const formatWeekDate = (start: Date, end: Date) => {
      return `${start.getDate()}-${end.getDate()}`
    }

    return (
      <div className={classes.container}>
        {/* Week headers */}
        <div className={classes.flex}>
          <div className={`${classes.flex} ${classes.flex1}`}>
            {weeks.map((week, index) => {
              const isTodayInWeek = isTodayInPeriod(week.start, week.end)
              return (
                <div
                  key={index}
                  style={{ width: `${cellWidth}px` }}
                  className={classNames({
                    [classes.dayBlock]: true,
                    [classes.dayBlockCompact]: isCompact,
                    [classes.isTodayBackgroundColor]: isTodayInWeek
                  })}
                >
                  <div
                    className={classNames(classes.weatherTextContainer, {
                      [classes.weatherTextContainerCompact]: isCompact
                    })}
                  >
                    <Typography variant={'subtitle1'} className={classNames({ [classes.isTodayText]: isTodayInWeek })}>
                      {formatWeekLabel(week.start)}
                    </Typography>
                    <Typography
                      variant={'h4'}
                      weight={'normal'}
                      className={classNames({ [classes.isTodayText]: isTodayInWeek })}
                    >
                      {formatWeekDate(week.start, week.end)}
                    </Typography>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  if (zoomLevel === 'months') {
    const months: Date[] = []
    const startMonth = new Date(startDate.getFullYear(), startDate.getMonth(), 1)
    const endMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1)
    const currentDate = new Date(startMonth)

    while (currentDate <= endMonth) {
      months.push(new Date(currentDate))
      currentDate.setMonth(currentDate.getMonth() + 1, 1)
    }

    return (
      <div className={classes.container}>
        {/* Month headers */}
        <div className={classes.flex}>
          <div className={`${classes.flex} ${classes.flex1}`}>
            {months.map((month, index) => {
              const monthStart = new Date(month.getFullYear(), month.getMonth(), 1)
              const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0)
              const isTodayInMonth = isTodayInPeriod(monthStart, monthEnd)

              return (
                <div
                  key={index}
                  style={{ width: `${cellWidth}px` }}
                  className={classNames({
                    [classes.dayBlock]: true,
                    [classes.dayBlockCompact]: isCompact,
                    [classes.isTodayBackgroundColor]: isTodayInMonth
                  })}
                >
                  <div
                    className={classNames(classes.weatherTextContainer, {
                      [classes.weatherTextContainerCompact]: isCompact
                    })}
                  >
                    <div>
                      <Typography
                        variant={'subtitle1'}
                        className={classNames({ [classes.isTodayText]: isTodayInMonth })}
                      >
                        {month.toLocaleDateString('en-US', { month: 'short' })}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant={'body1'} className={classNames({ [classes.isTodayText]: isTodayInMonth })}>
                        {month.getFullYear()}
                      </Typography>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  if (zoomLevel === 'quarters') {
    const quarters: Array<{ year: number; quarter: number; start: Date; end: Date }> = []
    const startQuarterMonth = Math.floor(startDate.getMonth() / 3) * 3
    const endQuarterMonth = Math.floor(endDate.getMonth() / 3) * 3
    const startQuarterDate = new Date(startDate.getFullYear(), startQuarterMonth, 1)
    const endQuarterDate = new Date(endDate.getFullYear(), endQuarterMonth, 1)
    const currentDate = new Date(startQuarterDate)

    while (currentDate <= endQuarterDate) {
      const year = currentDate.getFullYear()
      const quarter = Math.floor(currentDate.getMonth() / 3) + 1
      const quarterStart = new Date(year, (quarter - 1) * 3, 1)
      const quarterEnd = new Date(year, quarter * 3, 0)

      quarters.push({ year, quarter, start: quarterStart, end: quarterEnd })
      currentDate.setMonth(currentDate.getMonth() + 3, 1)
    }

    return (
      <div className={classes.container}>
        {/* Quarter headers */}
        <div className={classes.flex}>
          <div className={`${classes.flex} ${classes.flex1}`}>
            {quarters.map((q, index) => {
              const isTodayInQuarter = isTodayInPeriod(q.start, q.end)

              return (
                <div
                  key={index}
                  style={{ width: `${cellWidth}px` }}
                  className={classNames({
                    [classes.dayBlock]: true,
                    [classes.dayBlockCompact]: isCompact,
                    [classes.isTodayBackgroundColor]: isTodayInQuarter
                  })}
                >
                  <div
                    className={classNames(classes.weatherTextContainer, {
                      [classes.weatherTextContainerCompact]: isCompact
                    })}
                  >
                    <Typography
                      variant={'subtitle1'}
                      className={classNames({ [classes.isTodayText]: isTodayInQuarter })}
                    >
                      Q{q.quarter}
                    </Typography>
                    <Typography variant={'body1'} className={classNames({ [classes.isTodayText]: isTodayInQuarter })}>
                      {q.year}
                    </Typography>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  // Return null for non-"days" zoom levels to avoid missing return error
  return <></>
}

export default TimelineHeader
